import {
	GermanXmlExportRpcData,
	GermanXmlValidationResult,
	GermanPositionType,
	GermanPosition,
	COUNTRY_GERMAN_NAMES,
	AbrechnungsbereichType,
} from './types';
import { euroToCent, quantityToMilli, truncateDescription } from './german-xml-generator';

export type IsBelCodeFn = (codeE: string) => boolean;

export function determinePositionType(
	item: GermanXmlExportRpcData['job_items'][0],
	isBelCode: IsBelCodeFn
): GermanPositionType {
	if (item.type === 0) {
		if (isBelCode(item.code_e)) {
			return 'BEL';
		}
		return 'NBL';
	}

	if (item.type === 1) {
		if (item.cluster === '2200') {
			return 'EDM';
		}
		return 'MAT';
	}

	if (item.type === 4) {
		return 'RBT';
	}

	return 'NBL';
}

export function getHerstellungsort(org: { country: string; city: string }): string {
	if (org.country === 'de') {
		return `D-${org.city}`;
	}
	return COUNTRY_GERMAN_NAMES[org.country] || org.country;
}

export function getAbrechnungsbereich(
	org: { country: string; billing_region: string | null },
	client: { billing_region: string | null }
): AbrechnungsbereichType | null {
	if (org.country === 'de') {
		return org.billing_region as AbrechnungsbereichType;
	}
	return client.billing_region as AbrechnungsbereichType | null;
}

export function validateGermanExportData(
	data: GermanXmlExportRpcData,
	auftragsnummer: string,
	isBelCode: IsBelCodeFn,
	isGermanRegion: boolean
): GermanXmlValidationResult {
	const errors: string[] = [];
	const warnings: string[] = [];

	if (!isGermanRegion) {
		errors.push('Deutscher XML-Export nur für deutsche Region verfügbar');
	}

	if (!data.invoice) {
		errors.push('Rechnung muss vor XML-Export erstellt werden');
	}

	if (data.job_document.type !== 'dn') {
		errors.push('Lieferschein muss vor XML-Export existieren');
	}

	const billingRegion = getAbrechnungsbereich(data.organization, data.client);
	if (!billingRegion) {
		if(data.organization.country === 'de') {
		errors.push('Abrechnungsbereich muss unter "Mein Labor" > "Administration" gesetzt sein');
		} else {
			errors.push('Abrechnungsbereich muss in den Kundeneinstellungen gesetzt sein');
		}
	}

	if (!data.organization.city) {
		errors.push('Laborstadt muss für Herstellungsort gesetzt sein');
	}

	if (!data.job_items || data.job_items.length === 0) {
		errors.push('Keine Positionen zum Exportieren');
	}

	const nonRbtItems = data.job_items?.filter(item => determinePositionType(item, isBelCode) !== 'RBT') ?? [];
	if (data.job_items && data.job_items.length > 0 && nonRbtItems.length === 0) {
		errors.push('Export kann nicht nur aus Rabatten bestehen');
	}

	if (auftragsnummer.length > 50) {
		errors.push('Auftragsnummer darf maximal 50 Zeichen haben');
	}

	if (!auftragsnummer.trim()) {
		errors.push('Auftragsnummer ist erforderlich');
	}

	for (const item of data.job_items ?? []) {
		if (item.description && item.description.length > 60) {
			warnings.push(`Position ${item.code_e || item.description.slice(0, 20)}: Beschreibung auf 60 Zeichen gekürzt`);
		}
	}


	return {
		isValid: errors.length === 0,
		errors,
		warnings,
	};
}

export function calculateItemAmount(item: GermanXmlExportRpcData['job_items'][0]): number {
	const baseAmount = item.price * item.quantity * (item.tp_value || 1);
	if (item.discount && item.discount > 0) {
		return baseAmount * (1 - item.discount / 100);
	}
	return baseAmount;
}

export function mapJobItemsToPositions(
	jobItems: GermanXmlExportRpcData['job_items'],
	isBelCode: IsBelCodeFn
): GermanPosition[] {
	return jobItems.map(item => {
		const art = determinePositionType(item, isBelCode);
		const beschreibung = truncateDescription(item.description || '');

		const effectiveUnitPrice = item.price * (item.tp_value || 1);
		const discountMultiplier = item.discount && item.discount > 0 ? (1 - item.discount / 100) : 1;
		const finalUnitPrice = effectiveUnitPrice * discountMultiplier;

		const position: GermanPosition = {
			art,
			beschreibung,
			einzelpreis: euroToCent(finalUnitPrice),
			menge: quantityToMilli(item.quantity),
		};

		if (art === 'BEL' && item.code_e) {
			position.nummer = item.code_e;
		}

		return position;
	});
}

export interface DocumentDiscounts {
	discountWork: number;
	discountMaterial: number;
}

export function calculateDocumentDiscountPositions(
	jobItems: GermanXmlExportRpcData['job_items'],
	documentDiscounts: DocumentDiscounts,
	_isBelCode: IsBelCodeFn
): GermanPosition[] {
	const positions: GermanPosition[] = [];

	if (documentDiscounts.discountWork > 0) {
		const workItems = jobItems.filter(item => item.type === 0 || item.type === 5);
		const totalWorkAmount = workItems.reduce((sum, item) => sum + calculateItemAmount(item), 0);
		const discountAmount = totalWorkAmount * (documentDiscounts.discountWork / 100);

		if (discountAmount > 0) {
			positions.push({
				art: 'RBT',
				beschreibung: `Rabatt Arbeit ${documentDiscounts.discountWork}%`,
				einzelpreis: euroToCent(discountAmount),
				menge: 1000,
			});
		}
	}

	if (documentDiscounts.discountMaterial > 0) {
		const materialItems = jobItems.filter(item => item.type === 1);
		const totalMaterialAmount = materialItems.reduce((sum, item) => sum + calculateItemAmount(item), 0);
		const discountAmount = totalMaterialAmount * (documentDiscounts.discountMaterial / 100);

		if (discountAmount > 0) {
			positions.push({
				art: 'RBT',
				beschreibung: `Rabatt Material ${documentDiscounts.discountMaterial}%`,
				einzelpreis: euroToCent(discountAmount),
				menge: 1000,
			});
		}
	}

	return positions;
}
